import { Link, Tooltip } from '@material-ui/core';
import { Error, WarningRounded } from '@material-ui/icons';
import {
  Color,
  Column,
  Columns,
  Inline,
  Stack,
  Tag,
  useResponsiveValue,
} from '@superdispatch/ui';
import { Box, SidebarSubheader } from '@superdispatch/ui-lab';
import { Sidebar } from 'shared/layout/Sidebar';
import { Navigate, useParams } from 'react-router-dom';
import { SuperCarrierBadgeIcon } from 'shared/icons/SuperCarrierBadgeIcon';
import {
  useCarrierInfo,
  useIsCarrierWithoutUSDOT,
} from 'shared/modules/carrier-profile/data/CarrierInfoAPI';
import { useVerifiedCarrierApplicationStatus } from 'shared/modules/carrier-profile/data/VerifiedCarrierApplicationStatusAPI';
import { VerifiedCarrierApplicationStatusDTO } from 'shared/modules/carrier-profile/data/VerifiedCarrierApplicationStatusDTO';
import { SidebarMenuItemLink } from 'shared/routing/SidebarMenuItemLink';
import { useCarrierSettings } from 'shared/settings/CarrierSettingsAPI';
import {
  getInsuranceExpiredForUnverifiedCarrier,
  getInsuranceExpiring,
} from './core/CargoInsuranceExpiring';
import { VerifiedCarrierApplicationStatus } from './core/VerifiedCarrierApplicationStatus';

type VerificationStatuses =
  VerifiedCarrierApplicationStatusDTO['verified_carrier_application']['status'];
export const phoneVerificationStatuses: VerificationStatuses[] = [
  'pending',
  'non_verifiable',
  'not_submitted',
];

const carrierInfoPages = [
  'overview',
  'carrier_info',
  'cargo_insurance',
  'w9',
  'transporter_plate_registration',
  'state_motor_carrier_permit',
  'business_license',
] as const;

const paymentPages = ['superpay', 'billing_info', 'ach_payment'] as const;

type CarrierInfoPage = (typeof carrierInfoPages)[number];
type PaymentPage = (typeof paymentPages)[number];
type ProfilePageName = CarrierInfoPage | PaymentPage | 'terms_and_conditions';

function isValidProfilePageName(value: string): value is ProfilePageName {
  switch (value) {
    case 'overview':
    case 'carrier_info':
    case 'cargo_insurance':
    case 'w9':
    case 'transporter_plate_registration':
    case 'state_motor_carrier_permit':
    case 'business_license':
    case 'billing_info':
    case 'ach_payment':
    case 'superpay':
    case 'terms_and_conditions':
      return true;
    default:
      return value.startsWith('superpay') || value.startsWith('ach_payment');
  }
}

function VerifiedCarrierBadge() {
  return (
    <Tooltip
      interactive={true}
      title={
        <>
          You are a Verified Carrier.{' '}
          <Link
            href="https://support.superdispatch.com/hc/articles/35661884290707-What-is-Verified-Carrier-Status"
            target="_blank"
            rel="noopener noreferrer"
            color="inherit"
          >
            Learn More
          </Link>
        </>
      }
    >
      <SuperCarrierBadgeIcon htmlColor={Color.Green300} />
    </Tooltip>
  );
}

export function ProfileSidebar() {
  const { '*': step = '' } = useParams();
  const isCarrierWithoutUSDOT = useIsCarrierWithoutUSDOT();
  const isMobile = useResponsiveValue(true, false);
  const { data: carrierSettings } = useCarrierSettings();
  const { data: carrierInfo } = useCarrierInfo();
  const { data } = useVerifiedCarrierApplicationStatus();
  const verifiedCarrierApplication = data?.verified_carrier_application;
  const reasons = verifiedCarrierApplication?.reasons;
  const isVerified = verifiedCarrierApplication?.status === 'approved';
  const superPayVerificationStatus = carrierSettings?.superpay.verification;
  const isSuperPayVerified = superPayVerificationStatus === 'verified';
  const isSuperPayOnboardingNotCompleted = isVerified && !isSuperPayVerified;
  const isSuperPayOnboardingError =
    superPayVerificationStatus === 'bank_account_failed' ||
    superPayVerificationStatus === 'bank_account_errored';
  const isSuperPayBankError = !!carrierSettings?.superpay.payment_error;
  const isSuperPayError = isSuperPayOnboardingError || isSuperPayBankError;
  const isSuperPayToDo = !isSuperPayError && isSuperPayOnboardingNotCompleted;
  const isInsuranceNeedsRenewal =
    isVerified &&
    !carrierSettings?.carrier.is_insurance_renewal_initiated &&
    !!carrierSettings?.carrier.cargo_insurance_expiration_date &&
    getInsuranceExpiring(
      carrierSettings.carrier.cargo_insurance_expiration_date,
    );
  const isInsuranceExpiredForUnverifiedCarrier =
    carrierSettings &&
    getInsuranceExpiredForUnverifiedCarrier(carrierSettings.carrier);
  const isW9WasRemoved =
    verifiedCarrierApplication?.status !== 'not_submitted' &&
    !verifiedCarrierApplication?.progress.w9_step_is_complete;

  const isFmcsaPhoneNeedVerification =
    !carrierInfo?.fmcsa_phone_number?.is_verified &&
    verifiedCarrierApplication &&
    !phoneVerificationStatuses.includes(verifiedCarrierApplication.status);

  const hasCarrierInfoError =
    !!reasons?.fmcsa_phone.length ||
    !!carrierInfo?.fmcsa_phone_number?.grace_period_ends_at;

  if (!isValidProfilePageName(step) && !isMobile) {
    return <Navigate to="/profile/overview" replace={true} />;
  }

  return (
    <Sidebar
      title={
        <Columns align="center">
          <Column>Profile</Column>

          {isVerified && (
            <Column width="content">
              <VerifiedCarrierBadge />
            </Column>
          )}
        </Columns>
      }
      header={data && <VerifiedCarrierApplicationStatus {...data} />}
    >
      <Stack space="small">
        <Box>
          <SideBarMenuItem
            page="overview"
            title="Overview"
            hasError={
              !!reasons?.reliability_score.length ||
              !!reasons?.terms_and_conditions.length
            }
          />

          <SideBarMenuItem
            page="carrier_info"
            title="Carrier Info"
            hasError={hasCarrierInfoError}
            shouldShowToDoTag={
              isFmcsaPhoneNeedVerification && !hasCarrierInfoError
            }
          />

          <SideBarMenuItem
            page="cargo_insurance"
            title="Cargo Insurance"
            hasError={
              !!reasons?.insurance.length ||
              isInsuranceExpiredForUnverifiedCarrier
            }
            hasWarning={
              verifiedCarrierApplication?.status === 'reviewed' ||
              isInsuranceNeedsRenewal
            }
          />

          <SideBarMenuItem
            page="w9"
            title="W-9 Form"
            hasError={!!reasons?.w9.length || isW9WasRemoved}
          />

          {carrierInfo?.logistics_type === 'driveaway' && (
            <SideBarMenuItem
              page="transporter_plate_registration"
              title="Transporter Plate Registration"
              hasError={!!reasons?.transporter_plate_registration.length}
            />
          )}

          {isCarrierWithoutUSDOT &&
            (carrierInfo?.logistics_type === 'auto_hauler' ||
              carrierInfo?.logistics_type === 'towing') && (
              <SideBarMenuItem
                page="state_motor_carrier_permit"
                title="State Motor Carrier Permit"
                hasError={!!reasons?.state_motor_carrier_permit.length}
              />
            )}

          {isCarrierWithoutUSDOT &&
            carrierInfo?.logistics_type === 'driveaway' && (
              <SideBarMenuItem
                page="business_license"
                title="Business License"
                hasError={!!reasons?.business_license.length}
              />
            )}
        </Box>

        <Box>
          <SidebarSubheader>Payments</SidebarSubheader>

          <SideBarMenuItem
            page="superpay"
            title="SuperPay"
            hasError={isSuperPayError}
            shouldShowToDoTag={isSuperPayToDo}
          />

          <SideBarMenuItem page="billing_info" title="Billing Info" />

          <SideBarMenuItem page="ach_payment" title="ACH Payment Info" />
        </Box>

        <SideBarMenuItem
          page="terms_and_conditions"
          title="Terms and Conditions"
        />
      </Stack>
    </Sidebar>
  );
}

interface SideBarMenuItemProps {
  page: ProfilePageName;
  title: string;
  hasError?: boolean;
  hasWarning?: boolean;
  shouldShowNewTag?: boolean;
  shouldShowToDoTag?: boolean;
}

function SideBarMenuItem({
  page,
  title,
  hasError,
  hasWarning,
  shouldShowNewTag,
  shouldShowToDoTag,
}: SideBarMenuItemProps) {
  const pageLink =
    page === 'superpay'
      ? `/profile/${page}?utm_medium=SuperPay Menu`
      : `/profile/${page}`;

  return (
    <SidebarMenuItemLink
      key={page}
      to={pageLink}
      action={
        <Inline verticalAlign="center">
          {shouldShowNewTag && (
            <Tag color="blue" variant="subtle">
              New
            </Tag>
          )}
          {shouldShowToDoTag && (
            <Tag color="yellow" variant="subtle">
              To Do
            </Tag>
          )}
          {hasError ? (
            <Error
              aria-label={`${title} Error`}
              htmlColor={Color.Red300}
              fontSize="small"
            />
          ) : hasWarning ? (
            <WarningRounded
              aria-label={`${title} Warning`}
              htmlColor={Color.Yellow300}
              fontSize="small"
            />
          ) : undefined}
        </Inline>
      }
    >
      {title}
    </SidebarMenuItemLink>
  );
}
